/**
 * <mellow-phone>: country select + national number. `.value` is the number in E.164
 * format ("+491511234567", or "" when empty) for wire:model.
 */
export class MellowPhone extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        // The inner select and input fire their own events; re-emit them as one value.
        this.addEventListener('input', this.onInner, true);
        this.addEventListener('change', this.onInner, true);
    }

    get select() {
        return this.querySelector('[data-mellow-phone-country]');
    }

    get input() {
        return this.querySelector('[data-mellow-phone-number]');
    }

    get dial() {
        const code = this.select?.value;
        const option = this.select?.querySelector(`[role="option"][data-value="${code}"]`);
        return option?.dataset.display?.replace(/\D/g, '') ?? '';
    }

    get value() {
        const digits = (this.input?.value ?? '').replace(/\D/g, '').replace(/^0+/, '');
        return digits ? `+${this.dial}${digits}` : '';
    }

    set value(value) {
        const digits = String(value ?? '').replace(/\D/g, '');
        if (!digits || !String(value).trim().startsWith('+')) {
            if (this.input) this.input.value = digits;
            return;
        }

        // Longest matching dialling code; keep the current country when it matches (+1 US/CA …).
        const options = [...(this.select?.querySelectorAll('[role="option"]') ?? [])];
        const candidates = options
            .map((option) => ({ code: option.dataset.value, dial: (option.dataset.display ?? '').replace(/\D/g, '') }))
            .filter(({ dial }) => dial && digits.startsWith(dial))
            .sort((a, b) => b.dial.length - a.dial.length || (b.code === this.select.value) - (a.code === this.select.value));

        const match = candidates[0];
        if (match && this.select) this.select.value = match.code;
        if (this.input) this.input.value = match ? digits.slice(match.dial.length) : digits;
        this.syncHidden();
    }

    onInner = (event) => {
        // Only handle the committed values of the select and the number input. Events inside
        // the select (its search field) must reach the select, which handles them itself.
        if (event.target !== this.select && event.target !== this.input) return;
        event.stopPropagation();

        this.syncHidden();
        this.dispatchEvent(new CustomEvent(event.type, { bubbles: true, detail: this.value }));
    };

    syncHidden() {
        const hidden = this.querySelector('[data-mellow-phone-value]');
        if (hidden && hidden.value !== this.value) hidden.value = this.value;
    }
}
